define('datakit/lib/resource.relationship',['require','exports','module','underscore','backbone','marionette','backbone.radio','bluebird','./belongs.to.relationship.type','./extracted.relationship.type'],function(require, exports, module) {
  var _                              = require('underscore');
  var Backbone                       = require('backbone');
  var Mn                             = require('marionette');
  var Radio                          = require('backbone.radio');
  var Promise                        = require('bluebird');
  var BelongsToRelationshipType      = require('./belongs.to.relationship.type');
  var ExtractedRelationshipType      = require('./extracted.relationship.type');
  var modelsChannel                  = Radio.channel('models');

  var RELATIONSHIP_TYPES_NAME = 'relationshipTypes';

  var DEFAULT_RELATIONSHIP_TYPES = {
    belongsTo: BelongsToRelationshipType,
    extracted: ExtractedRelationshipType
  };

  var FETCH_MODES = {
    // fetch the relationship only when the resource
    // has been requested
    LAZY_LOAD: 'lazyLoad',
    // fetch the relationship as soon as possible
    EAGER_FETCH: 'eagerFetch',
    // the relationship must be manually fetched
    MANUAL: 'manual'
  };

  var ResourceRelationship = Mn.Object.extend({

    options: {
      type: null,
      // determines the key that should be used to relate with this relationship.
      // this can be found on the foreignModel or the relationship resource depending on
      // the relationship type
      foreignKey: null,
      // determines which relationships are effected
      // create, read, or update sync events by this relationship
      downstreamCascadedRelationships: null,
      // determines if the resource should be cleared when
      // the relationship becomes stale
      clearOnStale: false,
      // determines if the resource should be cleared when
      // the relationship becomes corrupt
      // a relationship becomes corrupt when its relationship
      // is stale but can no longer fetch to get new data
      clearOnCorrupt: true,
      // determines how the relationship should be fetched
      // lazyLoad: fetch the relationship when it is requested
      // manual: fetch the relationship on your own time
      fetchMode: FETCH_MODES.LAZY_LOAD,
      // can be a class or a string which represents
      // the type of the resource that is connected
      // by the relationship
      resourceClass: null
    },

    initialize: function() {
      this.mergeOptions(this.options, [
        'name',
        'foreignModel',
        'onSync'
      ]);

      var relationship          = this;
      var originalResourceClass = this.getOption('resourceClass');
      var resourceClass         = this.getResourceClass();

      // if the resource class does not exist then return
      if (!_.isFunction(resourceClass)) {
        if (_.isString(originalResourceClass)) {
          throw new Error('The resource class "' + originalResourceClass 
            + '" for the relationship "' + this.name + '" could not be found.');
        }
        else {
          throw new Error('The resource class for the relationship "'
            + this.name + '" could not be found.');
        }
      }

      var originalFetchMethod = this.originalFetchMethod = resourceClass.prototype.fetch;

      resourceClass = resourceClass.extend({
        useHalSelfLinkAsUrl: false,
        fetch: function(options) {
          if (relationship.shouldUseOriginalResourceFetch(this)) {
            return originalFetchMethod.apply(this, arguments);
          }
          else {
            return relationship.fetchResource(options);
          }
        }
      });

      this.resource = new resourceClass(null, _.extend(
        {},
        this.getOption('resourceOptions'),
        {
          parentRelationship: this
        }
      ));
      this.resource.parentRelationship = this;

      var relationshipTypeClass =  this.getRelationshipTypeClass();

      if (!relationshipTypeClass) {
        var relationshipTypeName = this.getOption('type');
        if (_.isString(relationshipTypeName)) {
          throw new Error('The relationship type "' + relationshipTypeName 
            + '" could not be found for the relationship "'
            + this.name + '".');
        }
        else {
          throw new Error('A relationship type class' 
            + ' could not be found for the relationship "'
            + this.name + '".');
        }
      }

      this.relationshipType = new relationshipTypeClass(_.extend({}, this.options, {
        resource: this.resource,
        relationship: this
      }));

      _.extend(this, {
        boundAttributePaths: {},
        fetching: false,
        fetched: false,
        isStale: false
      });

      this.listenTo(this.resource, 'sync', function(resource, response, options) {
        var syncMethod = String(_.result(options, 'syncMethod')).toLowerCase();
        if (syncMethod == 'create' || syncMethod == 'update' || syncMethod == 'destroy') {
          // when any create/update/destroy happens, then cascaded relationships
          // will get effected
          ResourceRelationship.handleDownstreamCascadedRelationships(
            this.foreignModel,
            this.getOption('downstreamCascadedRelationships'))
        }
        Mn.triggerMethodOn(this, 'sync', this, response, options);
      });

      this.on('stale', function() {
        var clearOnStale = Mn._getValue(this.getOption('clearOnStale'), this);
        if (clearOnStale === true) {
          this.clearResource();
        }
      });

      this.on('corrupt', function() {
        var clearOnCorrupt = Mn._getValue(this.getOption('clearOnCorrupt'), this);
        if (clearOnCorrupt === true) {
          this.clearResource();
        }
      });

      this.resourceFetchAttempted = false;

      if (this.getOption('fetchMode') === FETCH_MODES.EAGER_FETCH) {
        this.foreignModel.fetchRelationship(this);
      }
    },

    isFetched: function() {
      return !!this.fetched;
    },

    onStale: function() {
      if (this.resourceFetchAttempted && this.getOption('fetchMode') !== FETCH_MODES.MANUAL) {
        this.foreignModel.fetchRelationship(this);
      }
    },

    setStale: function() {
      var isStale = !!this.isStale;
      this.isStale = true
      if (isStale !== true) {
        Mn.triggerMethodOn(this.resource, 'stale', this.resource, {
          relationshipName: this.name,
          relationship: this,
          relationshipType: this.relationshipType,
          foreignModel: this.foreignModel
        });
        Mn.triggerMethodOn(this.foreignModel, 'staleRelationship', this.foreignModel, {
          relationshipName: this.name,
          relationship: this,
          relationshipType: this.relationshipType,
          relationshipResource: this.resource
        });
        Mn.triggerMethodOn(this.foreignModel, 'staleRelationship:' + this.name, this.foreignModel, {
          relationshipName: this.name,
          relationship: this,
          relationshipType: this.relationshipType,
          relationshipResource: this.resource
        });
        Mn.triggerMethodOn(this, 'stale', this, {
          relationshipName: this.name,
          relationship: this,
          relationshipType: this.relationshipType,
          foreignModel: this.foreignModel
        });
      }
      return this;
    },

    shouldUseOriginalResourceFetch: function(resourceInstance) {
      return !!this.fetching || resourceInstance !== this.resource;
    },

    getRelationshipTypeClass: function() {
      return ResourceRelationship.getRelationshipTypeClass(
        this.getOption('resource'),
        this.getOption('type')
      );
    },

    getResourceClass: function() {
      return ResourceRelationship.getResourceClass(this.getOption('resourceClass'));
    },

    fetchResource: function(options) {
      var relationshipType = this.relationshipType;
      var resource         = this.resource

      options || (options = {});

      // isStale needs to be set here so that
      // future stale events cause this fetch to be
      // re-invoked
      var didTryFetchWhileStale = this.isStale;
      this.isStale = false;

      // we need to store that at least one
      // fetch was attempted
      this.resourceFetchAttempted = true;

      if (this.fetching === true) {
        // do not fetch again if fetching
        return this.lastFetchPromise;
      }

      if (!relationshipType.canFetchResource()) {
        if (didTryFetchWhileStale) {
          // if the relationship was stale but we could not fetch
          // that means all the data in the resource is bad
          Mn.triggerMethodOn(this, 'corrupt', this, {
            relationshipName: this.name,
            relationship: this,
            relationshipType: this.relationshipType,
            foreignModel: this.foreignModel
          });
          Mn.triggerMethodOn(this.resource, 'corrupt', this.resource, {
            relationshipName: this.name,
            relationship: this,
            relationshipType: this.relationshipType,
            foreignModel: this.foreignModel
          });
        }
        // we need to wait until we can actually fetch
        return;
      }

      // listen to the sync event because that is the best
      // way to know when a fetch was successful
      this.listenToOnce(resource, 'sync', function(model, response, options) {
        this.lastSyncOptions = options;
        this.lastFetchPromise = null;
        this.fetching = false;
        this.fetched = true;
      });

      this.fetching = true;

      // configure the relatioship
      relationshipType.configureResource();
      
      var request = relationshipType.fetchResource(options);

      Mn.triggerMethodOn(this, 'request', this, request, options);
      Mn.triggerMethodOn(relationshipType, 'request', relationshipType, request, options);

      return this.lastFetchPromise = Promise
          .resolve((request && request.promise) ? _.result(request, 'promise') : request)
          .bind(this)
          .tap(function(response) {
            Mn.triggerMethodOn(relationshipType, 'fetch:success', response, options);
            Mn.triggerMethodOn(this, 'fetch:success', this, response, options);
          })
          .catch(function(e) {
            Mn.triggerMethodOn(relationshipType, 'fetch:error', e, options);
            Mn.triggerMethodOn(this, 'fetch:error', this, e, options);
            throw e;
          })
          .finally(function(ctx) {
            Mn.triggerMethodOn(relationshipType, 'fetch:complete', options);
            Mn.triggerMethodOn(this, 'fetch:complete', this,  options);
          })
    },

    findLastResourceInDataPath: function(dataPath) {
      var foundResource = null;
      var remainingPath = null;
      var remainingArray = null;
      var dataArray = String(dataPath).split('.');
      var curPath;
      var curResource;

      for (var i = 0, len = dataArray.length; i < len; ++i) {
        curPath = dataArray.slice(0, i+1).join('.');
        curResource = this.resource.get(curPath);
        if ((curResource instanceof Backbone.Model) || (curResource instanceof Backbone.Collection)) {
          foundResource = curResource
        }
        else {
          remainingArray = dataArray.slice(i+1);
          if (remainingArray.length > 0) {
            remainingPath = remainingArray.join('.');
          }
        }
      }
      if (foundResource) {
        return {
          resource: foundResource,
          remainingPath: remainingPath 
        }
      }
    },

    addBoundAttributePath: function(attrPath, dataPath) {
      if (this.boundAttributePaths[attrPath] === true) { return; }

      var foreignModel = this.foreignModel;
      var result;

      if (dataPath) {
        result = this.findLastResourceInDataPath(dataPath);
      }

      if (result) {
        resource = result.resource;
      }
      else {
        resource = this.resource;
      }

      this.boundAttributePaths[attrPath] = true;

      function triggerEvents(options) {
        var value = dataPath ? this.resource.get(dataPath) : this.resource;
        foreignModel.trigger('change:' + attrPath, foreignModel, value, options);
        foreignModel.trigger('change', foreignModel, options);
      };

      if ((resource instanceof Backbone.Model) || (resource instanceof Backbone.Collection)) {
        var eventName = 'sync';
        if (result && result.remainingPath) {
          eventName += ' change:' + result.remainingPath;
        }
        this.listenTo(resource, eventName, function(_resource, resp, options) {
          triggerEvents.call(this, options)
        });
      }
      else {
        this.listenTo(this.resource, 'sync', function(_resource, resp, options) {
          triggerEvents.call(this, options);
        });
      }

      return this;
    },

    clearResource: function() {
      var resource = this.resource
      if (resource instanceof Backbone.Collection) {
        resource.reset();
      }
      else if (resource instanceof Backbone.Model) {
        resource.clear();
      }
      this.fetched = false;
    }
  },
  {
    getRelationshipTypeClass: function(resource, type) {
      if (!type) {
        return resource.defaultRelationshipTypeClass || BelongsToRelationshipType;
      }
      else if (_.isFunction(type)) {
        return type;
      }
      else if (_.isFunction(resource.getRelationshipTypeClass)) {
        return resource.getRelationshipTypeClass(type);
      }
      else {
        var relationshipTypes = _.result(resource, RELATIONSHIP_TYPES_NAME) || {};
        return relationshipTypes[type] || DEFAULT_RELATIONSHIP_TYPES[type] || null;
      }
    },
    getResourceClass: function(resourceClass) {
      if (_.isString(resourceClass)) {
        // resourceClass cloud be Model.Collection
        var parts = resourceClass.split('.');
        // will always be the model part
        var firstPart = parts[0];
        // could the Collection part
        var remainingParts = _.rest(parts);
        // resolve the resource class from the models channel
        resourceClass = modelsChannel.request(firstPart);
        while(resourceClass && remainingParts.length > 0) {
          resourceClass = resourceClass[remainingParts.shift()];
        }
      }
      return resourceClass;
    },

    handleDownstreamCascadedRelationships: function(foreignModel, downstreamCascadedRelationships) {
      if (!foreignModel || !_.isFunction(foreignModel.hasCreatedRelationship)) { return; }

      _.each(downstreamCascadedRelationships, function(relationshipName) {
        if (foreignModel.hasCreatedRelationship(relationshipName)) {
          foreignModel.getRelationship(relationshipName).setStale();
        }
      }, this);
    },
    
    DEFAULT_RELATIONSHIP_TYPES: DEFAULT_RELATIONSHIP_TYPES
  });

  _.extend(ResourceRelationship, FETCH_MODES);

  module.exports = ResourceRelationship;
});
